/* ------------------------------------------------------------------------
   Before / After — Used only by sections/before-after.liquid.
   ------------------------------------------------------------------------ */

class BeforeAfter extends HTMLElement {
  constructor() {
    super();

    this.raf = null;
    this.observer = null;
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.noHoverQuery = window.matchMedia("(hover: none)");

    this.onInput = this.onInput.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
  }

  connectedCallback() {
    this.range = this.querySelector("[data-ba-range]");
    this.stage = this.querySelector("[data-ba-stage]");

    if (!this.range || !this.stage) return;

    this.setupProducts();

    this.startValue = this.clamp(Number(this.dataset.start));
    this.valueTextTemplate = this.dataset.valueText || "";

    this.range.addEventListener("input", this.onInput);

    /* In the theme editor the section re-renders on every change, so the
       intro sweep is skipped to keep the preview stable. */
    if (window.Shopify && window.Shopify.designMode) {
      this.setPosition(this.startValue);
      this.classList.add("is-inview");
      return;
    }

    /* Reduced motion: land on the final position with no sweep. */
    if (this.motionQuery.matches) {
      this.setPosition(this.startValue);
      this.classList.add("is-inview");
      return;
    }

    this.setPosition(100);

    this.observer = new IntersectionObserver(this.onIntersect, {
      threshold: 0.25,
    });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.range?.removeEventListener("input", this.onInput);
    this.observer?.disconnect();
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);

    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /* Without hover there is nothing to reveal on, so the first tap opens the card */
  setupProducts() {
    this.products = Array.from(this.querySelectorAll("[data-ba-product]"));
    if (!this.products.length) return;

    this.products.forEach((product) => {
      product.addEventListener("click", (event) => {
        if (!this.noHoverQuery.matches) return;
        if (product.classList.contains("is-open")) return;

        event.preventDefault();
        this.closeProducts(product);
        product.classList.add("is-open");
      });
    });

    document.addEventListener("pointerdown", this.onDocumentPointerDown);
  }

  onDocumentPointerDown(event) {
    if (!this.products || !this.products.length) return;
    if (event.target.closest("[data-ba-product]")) return;

    this.closeProducts();
  }

  closeProducts(except) {
    this.products.forEach((product) => {
      if (product !== except) product.classList.remove("is-open");
    });
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      this.observer.disconnect();
      this.classList.add("is-inview");
      this.sweepTo(this.startValue);
    });
  }

  onInput() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    this.setPosition(this.clamp(Number(this.range.value)));
  }

  /* Animates the divider from its current spot to the configured start
     position once the section scrolls into view. */
  sweepTo(target) {
    const from = this.clamp(Number(this.range.value));
    const distance = target - from;
    const duration = 1100;
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.setPosition(from + distance * eased);

      this.raf = progress < 1 ? requestAnimationFrame(step) : null;
    };

    this.raf = requestAnimationFrame(step);
  }

  setPosition(value) {
    const next = this.clamp(value);
    const rounded = Math.round(next);

    this.style.setProperty("--ba-pos", `${next}%`);

    if (this.range.value !== String(rounded)) this.range.value = rounded;

    if (this.valueTextTemplate) {
      this.range.setAttribute(
        "aria-valuetext",
        this.valueTextTemplate
          .replace("[before]", String(rounded))
          .replace("[after]", String(100 - rounded))
      );
    }
  }

  clamp(value) {
    if (Number.isNaN(value)) return 50;
    return Math.min(100, Math.max(0, value));
  }
}

if (!customElements.get("before-after")) {
  customElements.define("before-after", BeforeAfter);
}
